import { TitleHolderFieldId } from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/Content/Content.constants';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { roundToFixedFloor } from 'utils/impounds';
import { checkIsChattelLoanPurpose } from 'utils/loanApplication';

import { selectLoanApplication } from './loanApplication.selectors';
import { selectCommonInfoLoanApplication } from './loanApplicationCommonInfo.selectors';
import {
  selectEntityChangedPropsById,
  selectLoanChangesHistory,
} from './loanChangesHistory.selectors';
import { selectIsCurrentUserCanChangeLoan } from './pendingChanges.selectors';

const selectClosing = createSelector(selectLoanApplication, ({ closing }) => closing);

export const selectClosingInfo = createSelector(selectClosing, ({ closingInfo }) => closingInfo);

export const selectIsLoanClosingTwoPart = createSelector(
  selectClosingInfo,
  closingInfo => closingInfo?.transactionDetailClosingType === 'TwoClosing',
);

const selectExactEntityChangedPropsById = memoizeOne(id =>
  createSelector([selectLoanChangesHistory], loanChangesHistory => {
    const entityChanges = loanChangesHistory[id];

    if (_.isNil(entityChanges)) {
      return {};
    }

    return [].concat(entityChanges).reduce((acc, { propertyName, newValue, oldValue }) => {
      const { initialValue } = acc[propertyName] || {};
      const isInitialValueSet = _.has(acc, `${propertyName}.initialValue`);

      acc[propertyName] = {
        newValue,
        initialValue: isInitialValueSet ? initialValue : oldValue,
      };

      return acc;
    }, {});
  }),
);

const TITLE_HOLDER_APPLICATION_FIELDS = new Set(Object.values(TitleHolderFieldId));

export const selectLoanClosingChangedPropsById = memoizeOne(
  ({ loanClosingId, loanId, applicationId }) =>
    createSelector(
      [
        selectExactEntityChangedPropsById(loanClosingId),
        selectEntityChangedPropsById(loanId),
        selectEntityChangedPropsById(applicationId),
      ],
      (loanClosingChangedProps, loanChangedProps, applicationChangedProps) => {
        const filteredApplicationChanges = _.pickBy(applicationChangedProps, (_value, key) =>
          TITLE_HOLDER_APPLICATION_FIELDS.has(key),
        );

        const result = {
          ...loanClosingChangedProps,
          ...loanChangedProps,
          ...filteredApplicationChanges,
        };

        if (loanChangedProps?.noteDate) {
          result.interestCreditNoteDate = loanChangedProps.noteDate;
        }

        if (loanChangedProps?.rescissionDate) {
          result.interestCreditRescissionDate = loanChangedProps.rescissionDate;
        }

        if (loanChangedProps?.fundingDate) {
          result.interestCreditFundingDate = loanChangedProps.fundingDate;
        }

        if (loanChangedProps?.disbursementDate) {
          result.interestCreditDisbursementDate = loanChangedProps.disbursementDate;
        }

        if (loanChangedProps?.firstPaymentDate) {
          result.interestCreditFirstPaymentDate = loanChangedProps.firstPaymentDate;
        }

        return result;
      },
    ),
  _.isEqual,
);

export const selectFundingInfo = createSelector(selectClosing, ({ fundingInfo }) => {
  if (!fundingInfo) {
    return {};
  }

  return {
    // Funding Information
    fundingInfoWarehouseBankId: fundingInfo.warehouseBankId,
    commitmentNumber: fundingInfo.commitmentNumber,
    fundingNumber: fundingInfo.fundingNumber,
    type: fundingInfo.type,
    fundingConfirmationNumber: fundingInfo.fundingConfirmationNumber,
    fundingInfoWireAmount: fundingInfo.wire?.amount,
    wireComments: fundingInfo.wire?.comments,
    wirePostedDate: fundingInfo.wire?.postedDate,
    curtailment: fundingInfo.wire?.curtailment,
    wireCalculationDetails: fundingInfo.wireToInformation?.calculationDetails,
    warehouseFundingOrdered: fundingInfo.fundingOrdered,
    // Closing Agent
    feeProviderId: fundingInfo.wireToInformation.closingAgent?.feeProviderId,
    companyName: fundingInfo.wireToInformation.closingAgent?.companyName,
    feeProviderLocationId: fundingInfo.wireToInformation.closingAgent?.feeProviderLocationId,
    closingAgentFeeProviderAddress: fundingInfo.wireToInformation.closingAgent?.companyAddress,
    bank: fundingInfo.wireToInformation.closingAgent?.bank,
    city: fundingInfo.wireToInformation.closingAgent?.city,
    state: fundingInfo.wireToInformation.closingAgent?.usState,
    closingAgentABANumber: fundingInfo.wireToInformation.closingAgent?.abaNumber,
    closingAgentAccountNumber: fundingInfo.wireToInformation.closingAgent?.accountNumber,
    referenceNumber: fundingInfo.wireToInformation.closingAgent?.referenceNumber,
    closingAgentContact: fundingInfo.wireToInformation.closingAgent?.contact,
    // Wire To Information
    wireToInfoWarehouseBankId: fundingInfo.wireToInformation.warehouseBankId,
    wireNumber: fundingInfo.wireToInformation.wireNumber,
    fedReferenceNumber: fundingInfo.wireToInformation.fedReferenceNumber,
    wireToInfoWireAmount: roundToFixedFloor(fundingInfo.wireToInformation.wireAmount ?? 0, 2),
    advanceAmountPercent: fundingInfo.wireToInformation.advanceAmountPercentage?.toFixed(2),
    advanceAmount: fundingInfo.wireToInformation.advanceAmount,
    addAdvanceAmount: fundingInfo.wireToInformation.addAdvanceAmount,
    // Further Credit To
    furtherCreditToABANumber: fundingInfo.wireToInformation.furtherCreditTo?.abaNumber,
    furtherCreditToAccountNumber: fundingInfo.wireToInformation.furtherCreditTo?.accountNumber,
    furtherCreditToContact: fundingInfo.wireToInformation.furtherCreditTo?.contact,
  };
});

export const selectWireAmountData = createSelector(
  selectFundingInfo,
  ({ wireCalculationDetails } = {}) => ({
    totalLoanAmount: wireCalculationDetails?.totalLoanAmount ?? 0,
    lenderChargedFeesTotal: wireCalculationDetails?.lenderPaidFeesTotal ?? 0,
    payoffsPaidToLenderTotal: wireCalculationDetails?.payoffLenderLiabilitiesSum ?? 0,
    hasPayoffsPaidToLender:
      _.isNil(wireCalculationDetails) ||
      !_.isNil(wireCalculationDetails.payoffLenderLiabilitiesSum),
    calculatedWireAmount: wireCalculationDetails?.calculatedWireAmount ?? 0,
  }),
);

export const selectWireAmountRows = createSelector(
  selectWireAmountData,
  ({
    totalLoanAmount,
    lenderChargedFeesTotal,
    payoffsPaidToLenderTotal,
    hasPayoffsPaidToLender,
  }) => [
    { label: 'Total Loan Amount', value: totalLoanAmount },
    { label: 'Lender-charged Fees', value: lenderChargedFeesTotal },
    ...(hasPayoffsPaidToLender
      ? [{ label: 'Payoffs paid to Lender', value: payoffsPaidToLenderTotal }]
      : []),
  ],
);

export const selectClosingCompanies = createSelector(selectClosingInfo, closingInfo =>
  closingInfo?.closingCompanies?.map(({ attendantCompany, location, ...rest }) => ({
    attendantCompanyId: attendantCompany?.id || '',
    type: attendantCompany?.type || '',
    ...location,
    ...rest,
  })),
);

export const selectClosingDocuments = createSelector(
  selectClosing,
  ({ closingDocuments }) => closingDocuments,
);

export const selectClosingAmortizationSchedule = createSelector(
  selectClosing,
  ({ closingAmortizationSchedule }) => closingAmortizationSchedule,
);

export const selectPostClosing = createSelector(selectClosing, ({ postClosing }) => {
  const {
    shippingInfo,
    closingInvestor,
    accountingInformation,
    servicerInfo,
    paymentInformation,
    min,
    closingDocumentRecords,
    investorConditions,
    preInvestorConditions,
    finalDocuments,
    shipperMilestone,
    mersInformation,
    flippedUnsalable,
  } = postClosing || {};

  const formattedClosingDocumentsRecords = closingDocumentRecords?.map((record, index) => {
    const stableKey = record.id ?? `doc-${index}`;

    return {
      ...record,
      id: stableKey,
      key: stableKey,
      finalCdRecordingAmount: record.finalCDRecordingAmount,
    };
  });

  return {
    ...shippingInfo,
    ...closingInvestor,
    ...accountingInformation,
    ...servicerInfo,
    ...paymentInformation,
    min,
    investorConditions: investorConditions ?? [],
    preInvestorConditions: preInvestorConditions ?? [],
    documentRecords: formattedClosingDocumentsRecords,
    finalDocuments: finalDocuments ?? null,
    shipperMilestone: shipperMilestone ?? null,
    mersInformation: mersInformation ?? null,
    flippedUnsalable: flippedUnsalable ?? null,
  };
});

export const selectIsPostClosingLoaded = createSelector(
  selectClosing,
  ({ postClosing }) => !_.isNil(postClosing),
);

export const selectClosingDocumentRecords = createSelector(
  selectPostClosing,
  ({ documentRecords }) => documentRecords,
);

export const selectWireInstructionConfirmation = createSelector(
  selectClosing,
  ({ wireInstructionConfirmation }) => wireInstructionConfirmation,
);

export const selectIsDisabledClosingInformationFields = createSelector(
  selectIsCurrentUserCanChangeLoan,
  isCurrentUserCanChangeLoan => !isCurrentUserCanChangeLoan,
);

// Funding dates cascade off the closing date and stay unavailable until the loan is locked;
// scheduling fields are editable from the start (HZ-3543). Chattel loans carry no prepaid
// interest (LEN-128) and fund weeks after docs, so their funding dates are editable unlocked (LEN-198).
export const selectIsDisabledLockGatedClosingFields = createSelector(
  selectIsDisabledClosingInformationFields,
  selectCommonInfoLoanApplication,
  (isDisabledClosingInformationFields, { lockExpirationDate, loanPurpose } = {}) =>
    isDisabledClosingInformationFields ||
    (!lockExpirationDate && !checkIsChattelLoanPurpose(loanPurpose)),
);
